import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header cart icon bubble — keeps the item count in sync with the cart,
 * even across client-side navigation / bfcache restores.
 */
class CartIconBubble extends Component {
  requiredRefs = ['cartCount'];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Listen for any cart add/update dispatched anywhere on the page
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Fix for the back-button / bfcache issue:
    // when the page is restored from bfcache, the DOM is stale — re-sync from server.
    window.addEventListener('pageshow', this.#onPageShow, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    const cart = event.detail?.resource;

    if (cart && typeof cart.item_count === 'number') {
      this.#setCount(cart.item_count);
    } else {
      // No cart data on the event (e.g. remove/error) — fetch fresh count
      this.#fetchCount();
    }
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) {
      // Page came from bfcache — DOM is stale, re-sync count from server
      this.#fetchCount();
    }
  };

  async #fetchCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.#setCount(cart.item_count);
    } catch (error) {
      console.error('Failed to refresh cart count:', error);
    }
  }

  /** @param {number} count */
  #setCount(count) {
    this.refs.cartCount.textContent = count.toString();
  }
}

if (!customElements.get('cart-icon-bubble')) {
  customElements.define('cart-icon-bubble', CartIconBubble);
}